import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Form, Modal, Alert, Badge } from 'react-bootstrap';
import { FaBuilding } from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { toast } from 'react-hot-toast';
import { api } from '../../../contexts/AuthContext';

const SIDEBAR_BLUE = '#60A5FA';

/** Format a Date or YYYY-MM-DD string as local calendar date (no timezone shift). */
const toDateKey = (value) => {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Parse YYYY-MM-DD into a local Date at noon (avoids DST edge cases). */
const parseDateKey = (key) => {
  if (!key) return null;
  const [y, m, d] = String(key).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 12, 0, 0, 0);
};

const emptyEntry = (dateKey = '') => ({
  date: dateKey,
  company: '',
  person: 'Me',
  hours: '',
  cost_per_hour: '20',
  notes: '',
});

const isMinePerson = (person) => {
  const name = (person || 'Me').trim().toLowerCase();
  return name === 'me' || name === 'εγώ' || name === 'εγω' || name === '';
};

const normalizePerson = (person) => {
  const name = (person || 'Me').trim();
  return name || 'Me';
};

const styles = {
  calendarView: {
    backgroundColor: '#1e1e1e',
    borderRadius: '8px',
    padding: '20px',
    minHeight: '500px',
  },
  calendarGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '10px',
    marginTop: '20px',
  },
  calendarDay: {
    backgroundColor: '#2d2d2d',
    borderRadius: '4px',
    padding: '10px',
    minHeight: '100px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: `1px solid ${SIDEBAR_BLUE}`,
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
  },
  calendarDayHeader: {
    fontWeight: 'bold',
    marginBottom: '10px',
    paddingBottom: '5px',
    borderBottom: '1px solid #444',
    color: '#fff',
  },
  timeEntry: {
    backgroundColor: '#3d3d3d',
    borderRadius: '4px',
    padding: '5px',
    marginBottom: '5px',
    fontSize: '0.8rem',
    cursor: 'pointer',
    color: '#fff',
  },
  summaryCard: {
    backgroundColor: '#2d2d2d',
    borderRadius: '8px',
    padding: '15px',
    marginTop: '20px',
    border: `1px solid ${SIDEBAR_BLUE}`,
  },
};

const TimeTracking = () => {
  const [timeEntries, setTimeEntries] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [newEntry, setNewEntry] = useState(emptyEntry());

  const fetchTimeEntries = useCallback(async () => {
    try {
      const response = await api.get('/api/time-entries/');
      setTimeEntries(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching time entries:', err);
      setError('Failed to fetch time entries');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await api.get('/api/companies/');
      setCompanies(response.data);
    } catch (err) {
      console.error('Error fetching companies:', err);
      toast.error('Failed to fetch companies');
    }
  }, []);

  useEffect(() => {
    fetchTimeEntries();
    fetchCompanies();
  }, [fetchTimeEntries, fetchCompanies]);

  const closeModal = () => {
    setShowEntryModal(false);
    setSelectedEntry(null);
    setNewEntry(emptyEntry());
  };

  const getEntriesForDay = (date) => {
    const key = toDateKey(date);
    return timeEntries.filter((entry) => toDateKey(entry.date) === key);
  };

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleDayClick = (date) => {
    setSelectedEntry(null);
    setNewEntry(emptyEntry(toDateKey(date)));
    setShowEntryModal(true);
  };

  const handleEntryClick = (entry) => {
    setSelectedEntry(entry);
    setNewEntry({
      date: toDateKey(entry.date),
      company: String(entry.company),
      person: entry.person || 'Me',
      hours: String(entry.hours),
      cost_per_hour: String(entry.cost_per_hour),
      notes: entry.notes || '',
    });
    setShowEntryModal(true);
  };

  const formatError = (err) => {
    const data = err.response?.data;
    if (!data) return 'Failed to save time entry';
    if (typeof data === 'string') return data;
    if (data.detail) return data.detail;
    if (data.non_field_errors) return data.non_field_errors.join(' ');
    const firstKey = Object.keys(data)[0];
    if (firstKey) {
      const val = data[firstKey];
      return `${firstKey}: ${Array.isArray(val) ? val.join(' ') : val}`;
    }
    return 'Failed to save time entry';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newEntry.date || !newEntry.company || !newEntry.hours || !newEntry.cost_per_hour) {
      toast.error('Please fill in all required fields');
      return;
    }

    const hours = parseFloat(newEntry.hours);
    const costPerHour = parseFloat(newEntry.cost_per_hour);

    if (isNaN(hours) || hours <= 0) {
      toast.error('Hours must be a positive number');
      return;
    }
    if (isNaN(costPerHour) || costPerHour <= 0) {
      toast.error('Cost per hour must be a positive number');
      return;
    }

    const payload = {
      date: toDateKey(newEntry.date),
      company: parseInt(newEntry.company, 10),
      person: normalizePerson(newEntry.person),
      hours: hours.toFixed(2),
      cost_per_hour: costPerHour.toFixed(2),
      notes: newEntry.notes || '',
    };

    try {
      setSaving(true);
      if (selectedEntry) {
        await api.put(`/api/time-entries/${selectedEntry.id}/`, payload);
        toast.success('Time entry updated');
      } else {
        const existing = timeEntries.find(
          (entry) =>
            toDateKey(entry.date) === payload.date &&
            String(entry.company) === String(payload.company) &&
            normalizePerson(entry.person) === payload.person
        );
        if (existing) {
          await api.put(`/api/time-entries/${existing.id}/`, payload);
          toast.success('Existing entry for this person/company/day updated');
        } else {
          await api.post('/api/add-time-entry/', payload);
          toast.success('Time entry created');
        }
      }
      closeModal();
      await fetchTimeEntries();
    } catch (err) {
      console.error('Error saving time entry:', err);
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedEntry) return;
    if (!window.confirm('Delete this time entry?')) return;
    try {
      await api.delete(`/api/time-entries/${selectedEntry.id}/`);
      setTimeEntries((prev) => prev.filter((entry) => entry.id !== selectedEntry.id));
      toast.success('Deleted');
      closeModal();
    } catch (err) {
      console.error('Error deleting time entry:', err);
      toast.error('Failed to delete time entry');
    }
  };

  const getMonthEntries = () => {
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    return timeEntries.filter((entry) => {
      const entryDate = parseDateKey(entry.date);
      if (!entryDate) return false;
      return entryDate.getMonth() === currentMonth && entryDate.getFullYear() === currentYear;
    });
  };

  const getPersonOptions = () => {
    const names = new Set(['Me']);
    timeEntries.forEach((entry) => {
      const name = normalizePerson(entry.person);
      if (name) names.add(name);
    });
    return Array.from(names).sort((a, b) => {
      if (a === 'Me') return -1;
      if (b === 'Me') return 1;
      return a.localeCompare(b);
    });
  };

  const getCompanySummary = () => {
    const summary = {};
    getMonthEntries().forEach((entry) => {
      const company = entry.company_name || `Company #${entry.company}`;
      const person = normalizePerson(entry.person);
      if (!summary[company]) {
        summary[company] = {
          totalHours: 0,
          totalCost: 0,
          byPerson: {},
        };
      }
      const hours = parseFloat(entry.hours) || 0;
      const cost = parseFloat(entry.total_cost) || 0;
      summary[company].totalHours += hours;
      summary[company].totalCost += cost;
      if (!summary[company].byPerson[person]) {
        summary[company].byPerson[person] = { totalHours: 0, totalCost: 0 };
      }
      summary[company].byPerson[person].totalHours += hours;
      summary[company].byPerson[person].totalCost += cost;
    });
    return summary;
  };

  const getPeopleTotals = () => {
    const totals = {};
    getMonthEntries().forEach((entry) => {
      const person = normalizePerson(entry.person);
      if (!totals[person]) {
        totals[person] = { totalHours: 0, totalCost: 0 };
      }
      totals[person].totalHours += parseFloat(entry.hours) || 0;
      totals[person].totalCost += parseFloat(entry.total_cost) || 0;
    });
    return totals;
  };

  const renderCalendarView = () => {
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
    const days = [];

    for (let i = 0; i < firstDayOfMonth; i += 1) {
      days.push(
        <div
          key={`empty-${i}`}
          style={{
            ...styles.calendarDay,
            backgroundColor: '#1e1e1e',
            cursor: 'default',
          }}
        />
      );
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = new Date(currentYear, currentMonth, day, 12, 0, 0, 0);
      const dayEntries = getEntriesForDay(date);
      const isToday = toDateKey(date) === toDateKey(new Date());

      days.push(
        <div
          key={day}
          style={{
            ...styles.calendarDay,
            backgroundColor: isToday ? '#2d2d2d' : '#1e1e1e',
          }}
          onClick={() => handleDayClick(date)}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#3d3d3d';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = isToday ? '#2d2d2d' : '#1e1e1e';
          }}
        >
          <div
            style={{
              ...styles.calendarDayHeader,
              color: isToday ? SIDEBAR_BLUE : '#fff',
            }}
          >
            {String(day).padStart(2, '0')}/{String(currentMonth + 1).padStart(2, '0')}
          </div>
          {dayEntries.map((entry) => (
            <div
              key={entry.id}
              style={styles.timeEntry}
              onClick={(e) => {
                e.stopPropagation();
                handleEntryClick(entry);
              }}
            >
              <div style={{ fontWeight: 600 }}>{entry.company_name}</div>
              <div style={{ color: isMinePerson(entry.person) ? SIDEBAR_BLUE : '#fbbf24', fontSize: '0.75rem' }}>
                {normalizePerson(entry.person)}
              </div>
              <div>{entry.hours}h · €{entry.total_cost}</div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 style={{ color: '#fff' }}>
            {new Date(currentYear, currentMonth).toLocaleString('en-GB', {
              month: 'long',
              year: 'numeric',
            })}
          </h4>
          <div>
            <Button
              variant="outline-primary"
              size="sm"
              className="me-2"
              onClick={handlePreviousMonth}
              style={{ borderColor: SIDEBAR_BLUE, color: SIDEBAR_BLUE }}
            >
              Previous Month
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              className="me-2"
              onClick={() => setCurrentDate(new Date())}
              style={{ borderColor: SIDEBAR_BLUE, color: SIDEBAR_BLUE }}
            >
              Today
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleNextMonth}
              style={{ borderColor: SIDEBAR_BLUE, color: SIDEBAR_BLUE }}
            >
              Next Month
            </Button>
          </div>
        </div>
        <div className="d-flex mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((label) => (
            <div
              key={label}
              style={{ flex: 1, textAlign: 'center', fontWeight: 'bold', color: '#fff' }}
            >
              {label}
            </div>
          ))}
        </div>
        <div style={styles.calendarGrid}>{days}</div>
      </div>
    );
  };

  const renderEntryModal = () => (
    <Modal
      show={showEntryModal}
      onHide={closeModal}
      size="lg"
      contentClassName="bg-dark text-light"
    >
      <Modal.Header closeButton closeVariant="white" style={{ borderBottom: '1px solid #444' }}>
        <Modal.Title>{selectedEntry ? 'Edit Time Entry' : 'New Time Entry'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label style={{ display: 'block' }}>Date</Form.Label>
            <DatePicker
              selected={parseDateKey(newEntry.date)}
              onChange={(date) =>
                setNewEntry({ ...newEntry, date: date ? toDateKey(date) : '' })
              }
              className="form-control bg-dark text-light border-secondary"
              dateFormat="dd/MM/yyyy"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Company</Form.Label>
            <Form.Select
              value={newEntry.company}
              onChange={(e) => setNewEntry({ ...newEntry, company: e.target.value })}
              className="bg-dark text-light border-secondary"
              required
            >
              <option value="">Select a company</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Who (you or συνεργάτης)</Form.Label>
            <Form.Control
              list="time-person-options"
              value={newEntry.person}
              onChange={(e) => setNewEntry({ ...newEntry, person: e.target.value })}
              className="bg-dark text-light border-secondary"
              placeholder="Me or collaborator name"
              required
            />
            <datalist id="time-person-options">
              {getPersonOptions().map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
            <Form.Text style={{ color: '#9ca3af' }}>
              Use <strong>Me</strong> for your hours, or type your collaborator’s name.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Hours</Form.Label>
            <Form.Control
              type="number"
              step="0.5"
              min="0.5"
              value={newEntry.hours}
              onChange={(e) => setNewEntry({ ...newEntry, hours: e.target.value })}
              className="bg-dark text-light border-secondary"
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Cost per Hour (€)</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              value={newEntry.cost_per_hour}
              onChange={(e) => setNewEntry({ ...newEntry, cost_per_hour: e.target.value })}
              className="bg-dark text-light border-secondary"
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={newEntry.notes}
              onChange={(e) => setNewEntry({ ...newEntry, notes: e.target.value })}
              className="bg-dark text-light border-secondary"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #444' }}>
          {selectedEntry && (
            <Button variant="danger" onClick={handleDelete} disabled={saving}>
              Delete
            </Button>
          )}
          <Button variant="secondary" onClick={closeModal} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? 'Saving…' : selectedEntry ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );

  const renderCompanySummary = () => {
    const summary = getCompanySummary();
    const peopleTotals = getPeopleTotals();
    const totalHours = Object.values(summary).reduce((acc, data) => acc + data.totalHours, 0);
    const totalCost = Object.values(summary).reduce((acc, data) => acc + data.totalCost, 0);
    const myTotals = Object.entries(peopleTotals).reduce(
      (acc, [person, data]) => {
        if (isMinePerson(person)) {
          acc.totalHours += data.totalHours;
          acc.totalCost += data.totalCost;
        }
        return acc;
      },
      { totalHours: 0, totalCost: 0 }
    );
    const collaboratorEntries = Object.entries(peopleTotals).filter(
      ([person]) => !isMinePerson(person)
    );

    return (
      <Card
        style={{
          ...styles.summaryCard,
          marginBottom: '20px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        }}
      >
        <Card.Header
          style={{
            backgroundColor: '#1e1e1e',
            borderBottom: '1px solid #444',
            color: '#fff',
            padding: '15px 20px',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 16,
            }}
          >
            <h5 className="mb-0">Monthly Summary</h5>
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: '#888', fontSize: '0.85rem' }}>My hours</div>
                <div style={{ color: SIDEBAR_BLUE, fontSize: '1.15rem', fontWeight: 'bold' }}>
                  {myTotals.totalHours.toFixed(2)}h
                </div>
                <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                  €{myTotals.totalCost.toFixed(2)}
                </div>
              </div>
              {collaboratorEntries.map(([person, data]) => (
                <div key={person} style={{ textAlign: 'right' }}>
                  <div style={{ color: '#888', fontSize: '0.85rem' }}>{person}</div>
                  <div style={{ color: '#fbbf24', fontSize: '1.15rem', fontWeight: 'bold' }}>
                    {data.totalHours.toFixed(2)}h
                  </div>
                  <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                    €{data.totalCost.toFixed(2)}
                  </div>
                </div>
              ))}
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: '#888', fontSize: '0.85rem' }}>Combined</div>
                <div style={{ color: '#fff', fontSize: '1.15rem', fontWeight: 'bold' }}>
                  {totalHours.toFixed(2)}h
                </div>
                <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                  €{totalCost.toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        </Card.Header>
        <Card.Body style={{ padding: '20px' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '20px',
            }}
          >
            {Object.entries(summary).map(([company, data]) => (
              <div
                key={company}
                style={{
                  padding: '15px',
                  backgroundColor: '#1e1e1e',
                  borderRadius: '8px',
                  border: `1px solid ${SIDEBAR_BLUE}`,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px',
                  }}
                >
                  <h6 style={{ color: '#fff', margin: 0 }}>
                    <FaBuilding style={{ marginRight: '8px', color: SIDEBAR_BLUE }} />
                    {company}
                  </h6>
                  <Badge bg="primary" style={{ backgroundColor: SIDEBAR_BLUE }}>
                    {data.totalHours.toFixed(1)}h total
                  </Badge>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {Object.entries(data.byPerson).map(([person, personData]) => (
                    <div
                      key={person}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '6px 8px',
                        backgroundColor: '#2d2d2d',
                        borderRadius: 4,
                      }}
                    >
                      <span
                        style={{
                          color: isMinePerson(person) ? SIDEBAR_BLUE : '#fbbf24',
                          fontWeight: 600,
                          fontSize: '0.9rem',
                        }}
                      >
                        {person}
                      </span>
                      <span style={{ color: '#fff', fontSize: '0.9rem' }}>
                        {personData.totalHours.toFixed(1)}h · €{personData.totalCost.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: 12,
                    color: '#9ca3af',
                    fontSize: '0.85rem',
                  }}
                >
                  <span>
                    Avg €
                    {data.totalHours ? (data.totalCost / data.totalHours).toFixed(2) : '0.00'}/h
                  </span>
                  <span style={{ color: SIDEBAR_BLUE, fontWeight: 600 }}>
                    €{data.totalCost.toFixed(2)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card.Body>
      </Card>
    );
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 style={{ color: SIDEBAR_BLUE }}>Time Tracking</h2>
      </div>
      <Row>
        <Col>{renderCompanySummary()}</Col>
      </Row>
      <Row>
        <Col>
          <div style={styles.calendarView}>{renderCalendarView()}</div>
        </Col>
      </Row>
      {renderEntryModal()}
    </Container>
  );
};

export default TimeTracking;
